import type { AssistantMessageMeta, ThinkingState } from '../../meta/types';

/** How long a streaming turn can go without a new event before the status
 *  line says so. Single steps routinely run longer than this (generating a
 *  file, a large query), so crossing it does not mean failure: the line keeps
 *  saying the turn is still working and starts showing how long it has been
 *  since the server last sent anything. */
export const QUIET_AFTER_MS = 20_000;

/** Wall-clock bounds of the in-flight turn: when the request was sent, and
 *  when the stream last delivered an event. */
export interface TurnClock {
  readonly startedAt: number;
  readonly lastActivityAt: number;
}

export type TurnStatus =
  | { readonly kind: 'working'; readonly elapsedMs: number }
  | {
      readonly kind: 'quiet';
      readonly elapsedMs: number;
      readonly quietMs: number;
    };

export function turnStatus(clock: TurnClock, now: number): TurnStatus {
  const elapsedMs = Math.max(0, now - clock.startedAt);
  const quietMs = Math.max(0, now - clock.lastActivityAt);
  if (quietMs < QUIET_AFTER_MS) {
    return { kind: 'working', elapsedMs };
  }
  return { kind: 'quiet', elapsedMs, quietMs };
}

/** `42s`, `1m 5s`, `1h 3m`. Whole seconds; never negative. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes}m ${totalSeconds % 60}s`;
  }
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
}

/** A thinking block stopped short: the stream has closed but the server never
 *  sent `data-done`, the only event that completes a block. That covers a
 *  dropped connection, a server error, and the user pressing Stop. */
export function isThinkingInterrupted(
  thinking: Pick<ThinkingState, 'isComplete'>,
  isStreaming: boolean
): boolean {
  return !isStreaming && !thinking.isComplete;
}

/** True when any thinking block on the message stopped short. History
 *  hydration carries no thinking rows, so persisted messages never match. */
export function isTurnInterrupted(
  meta: AssistantMessageMeta | undefined,
  isStreaming: boolean
): boolean {
  if (!meta) {
    return false;
  }
  const blocks = meta.thinking
    ? [meta.thinking, ...meta.subAskThinkings]
    : meta.subAskThinkings;
  return blocks.some((t) => isThinkingInterrupted(t, isStreaming));
}
